import * as React from "react";
import { Handle, Position } from "@xyflow/react";
import { ChevronRightCircle, Github, Settings } from "lucide-react";
import { NavLink } from "react-router-dom";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserContext } from "@/context/user";
import { appConfigs, nodes, templates, NodeType } from "@/config/nodes";
import { Button } from "../ui/button";

type NodeData = {
  id: string;
  name: string;
  type: NodeType;
  completedMilestones: string[];
  template?: string;
  config?: Record<string, unknown>;
  onEdit?: (id: string) => void;
};

export const CustomNode = ({ data }: { data: NodeData }) => {
  const { user } = React.useContext(UserContext);
  return (
    <TooltipProvider>
      <div className="nodrag group relative w-[200px] rounded-lg border border-border bg-card text-card-foreground shadow-md">
        <Handle
          type="target"
          position={Position.Left}
          className="!h-3 !w-3 !-left-1.5 !border-2 !border-muted-foreground !bg-background"
        />
        <div className="flex items-center gap-2 border-b border-border p-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground">
                {nodes[data.type as keyof typeof nodes]?.icon}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="capitalize">{data.type}</p>
            </TooltipContent>
          </Tooltip>

          <h3 className="flex-1 truncate text-sm font-semibold text-foreground">
            {data.name}
          </h3>

          {data.onEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={'ghost'}
                  onClick={() => data.onEdit?.(data.id)}
                  className="cursor-pointer opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-all"
                  title="Edit node"
                >
                  <Settings size={13} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit node</p>
              </TooltipContent>
            </Tooltip>
          )}

          {data.completedMilestones?.includes("generate") && (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to={`https://github.com/${user?.data?.login}/${data.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Github size={16} />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent>
                <p>View on GitHub</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex min-h-[2.5rem] flex-wrap items-center gap-3 p-2 text-muted-foreground">
          {data.template && (
            <Tooltip>
              <TooltipTrigger>
                {templates[data.template as keyof typeof templates]?.icon}
              </TooltipTrigger>
              <TooltipContent>
                <p>{templates[data.template as keyof typeof templates]?.label}</p>
              </TooltipContent>
            </Tooltip>
          )}

          {data?.config &&
            Object.entries(data.config).map(([key, value]) => {
              if (!value) return null;

              if (key === "providers") {
                const selectedProviders =
                  appConfigs["providers"]?.options?.filter((option) =>
                    (value as string[]).includes(option.value),
                  ) || [];

                if (selectedProviders.length === 0) return null;

                return (
                  <Tooltip key={key}>
                    <TooltipTrigger className="flex items-center gap-1">
                      {selectedProviders.map((option, index) => (
                        <React.Fragment key={index}>
                          {option?.icon}
                        </React.Fragment>
                      ))}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {appConfigs["providers"]?.iconLabel}:{" "}
                        {selectedProviders
                          .map((option) => option?.label)
                          .join(", ")}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              const config = appConfigs[key as keyof typeof appConfigs];
              if (!config) return null;

              return (
                <Tooltip key={key}>
                  <TooltipTrigger>{config?.icon}</TooltipTrigger>
                  <TooltipContent>
                    <p>{config?.iconLabel}</p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
        </div>
        <Handle
          type="source"
          position={Position.Right}
          className="!h-3 !w-3 !-right-1.5 !border-2 !border-background !bg-muted-foreground"
        />
      </div>
    </TooltipProvider>
  );
};

const SimpleNode = ({ data }: { data: NodeData }) => (
  <TooltipProvider>
    <div className="nodrag relative w-[150px] rounded-md border border-border bg-card shadow-sm">
      <Handle
        type="target"
        position={Position.Left}
        className="!h-2 !w-2 !-left-1 !border !border-muted-foreground !bg-background"
      />

      <div className="border-b border-border p-1.5">
        <h3 className="truncate text-center text-xs font-semibold text-foreground">
          {data.name}
        </h3>
      </div>

      <div className="flex items-center justify-between p-2">
        <Tooltip>
          <TooltipTrigger>
            <span className="text-muted-foreground">
              {nodes[data.type as keyof typeof nodes]?.icon}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="capitalize">{data.type}</p>
          </TooltipContent>
        </Tooltip>

        <NavLink
          to={`./${data?.name}`}
          className="text-muted-foreground hover:text-foreground"
        >
          <ChevronRightCircle size={16} />
        </NavLink>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="!h-2 !w-2 !-right-1 !border !border-background !bg-muted-foreground"
      />
    </div>
  </TooltipProvider>
);

export const WebNode = SimpleNode;
export const MobileNode = SimpleNode;
export const ServiceNode = SimpleNode;
export const DatabaseNode = SimpleNode;
export const DesktopNode = SimpleNode;
export const WorkerNode = SimpleNode;
